// useAppVisibility — single source of truth for "the iOS tab just went
// to the background / came back to the foreground" lifecycle.
//
// Pilots routinely background the app for 5–60+ minutes (talking to
// ATC, looking at sectionals on paper, just locking the iPad in a
// holster). Without intervention, several systems keep running and
// drain battery / leak resources / silently lose auth:
//
//   • navigator.geolocation.watchPosition keeps firing at 1 Hz with
//     enableHighAccuracy=true. iOS doesn't always honor a paused tab
//     and the GPS chip stays hot. ~10–15 %/h battery drain.
//   • Traffic-target garbage-collection runs at 1 Hz unconditionally.
//   • Supabase JWT expires while hidden (default 1 h). Every write
//     after resume returns 401 → looks like the app is broken.
//   • Supabase realtime channels (FRAT, dip-sites, etc.) hold open
//     websockets in the background; on resume from a 30-min nap
//     they trigger a reconnect storm.
//
// This hook mounts ONCE at the top of AppShell and centralises all of
// that. Per the 2026-05-22 audit synthesis HI-3.
//
// Design notes:
// - We snapshot which subsystems were *running* when hidden so resume
//   doesn't accidentally start something the user had explicitly off.
// - We DON'T touch the wake-lock or the map renderer here; those have
//   their own visibility-aware logic.

import { useEffect } from 'react';
import { useGpsStore } from '../stores/gpsStore';
import { startTrafficGc, stopTrafficGc } from './traffic/store';
import { supabase } from './supabase';
import { useAuthStore } from '../stores/authStore';

export function useAppVisibility(): void {
  useEffect(() => {
    if (typeof document === 'undefined') return;

    // Snapshot of which subsystems were active at the moment we went
    // hidden, so resume restores them faithfully.
    let pausedGps = false;

    const handleHidden = () => {
      // GPS — pause the watchPosition. The store keeps the last fix
      // visible on the map; we just stop receiving updates.
      const gps = useGpsStore.getState();
      if (gps.watching) {
        pausedGps = true;
        gps.stop();
      } else {
        pausedGps = false;
      }

      // Traffic GC — stop the 1 Hz sweep. Stale targets will be
      // re-scored within a second of resume.
      stopTrafficGc();

      // Supabase realtime — disconnect all channels at once. Channels
      // themselves stay registered; on resume `realtime.connect()`
      // re-subscribes them.
      // Best-effort: swallow disconnect errors so a misbehaving
      // websocket can't block the visibilitychange handler.
      try {
        void supabase?.realtime.disconnect();
      } catch {
        /* ignore */
      }
    };

    const handleVisible = () => {
      // Refresh the JWT first — every subsequent write depends on it
      // being valid. On failure we set sessionStale=true so AppShell
      // can render a non-blocking re-authenticate banner (HI-5' fix).
      if (supabase) {
        supabase.auth
          .refreshSession()
          .then(() => {
            useAuthStore.getState().setSessionStale(false);
          })
          .catch(() => {
            useAuthStore.getState().setSessionStale(true);
          });
      }

      // Re-open realtime sockets. supabase-js will resubscribe every
      // channel that was previously joined.
      try {
        supabase?.realtime.connect();
      } catch {
        /* ignore */
      }

      // Resume traffic GC.
      startTrafficGc();

      // Resume GPS if it was active when we went hidden.
      if (pausedGps) {
        useGpsStore.getState().start();
        pausedGps = false;
      }
    };

    const onChange = () => {
      if (document.hidden) handleHidden();
      else handleVisible();
    };

    document.addEventListener('visibilitychange', onChange);

    // iOS Safari emits pageshow on bfcache restore without a
    // visibilitychange; treat it as a resume signal.
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) handleVisible();
    };
    window.addEventListener('pageshow', onPageShow);

    return () => {
      document.removeEventListener('visibilitychange', onChange);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, []);
}
